import React, { useState, useEffect } from 'react';

const slides = [
  {
    tag: '🌍 Sub-Saharan Africa Focus',
    title: 'Development Professional',
    subtitle: 'Driving financial inclusion across Africa through strategic program design and multi-country project management.',
    cta: 'Explore My Work',
    href: '/#experience',
    bg: 'linear-gradient(135deg, #0A2540 0%, #0D3060 55%, #0A3828 100%)',
  },
  {
    tag: '💡 Digital Finance Expert',
    title: 'Digital Financial Services',
    subtitle: 'Bridging the gap between technology and underserved communities — mobile money, digital lending, and inclusive product design.',
    cta: 'View Projects',
    href: '/#projects',
    bg: 'linear-gradient(135deg, #0A2540 0%, #0B2E50 58%, #063828 100%)',
  },
  {
    tag: '📊 Impact at Scale',
    title: 'Program Manager & Researcher',
    subtitle: 'Mastercard Foundation, Gates Foundation, USAID, UNCDF — managing donor-funded programs that empower youth, women, and MSMEs.',
    cta: 'Read My Blog',
    href: '/blog',
    bg: 'linear-gradient(135deg, #061829 0%, #0A2540 45%, #003828 100%)',
  },
];

const stats = [
  { value: '5+',    label: 'Years Experience' },
  { value: '8',     label: 'Countries' },
  { value: '1000+', label: 'Lives Impacted' },
  { value: '3',     label: 'Donor Programs' },
];

const E  = '#7C4DFF';
const ED = '#6030E8';

export default function HeroSlider() {
  const [current, setCurrent]   = useState(0);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => goTo((current + 1) % slides.length), 5500);
    return () => clearInterval(timer);
  }, [current]);

  const goTo = (idx) => {
    if (animating) return;
    setAnimating(true);
    setTimeout(() => { setCurrent(idx); setAnimating(false); }, 300);
  };

  const slide = slides[current];

  return (
    <section id="home" style={{
      minHeight: '100vh', background: slide.bg,
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      transition: 'background 0.8s ease', position: 'relative', overflow: 'hidden',
    }}>
      {/* Decorative circles */}
      <div style={{ position: 'absolute', width: 520, height: 520, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.05)', top: -110, right: -110, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', width: 320, height: 320, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.04)', top: 60, right: 60, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.02)', bottom: 100, left: -50, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(255,255,255,0.02) 1px, transparent 1px)', backgroundSize: '40px 40px', pointerEvents: 'none' }} />

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '120px 24px 80px', width: '100%' }}>
        <div style={{ opacity: animating ? 0 : 1, transform: animating ? 'translateY(20px)' : 'translateY(0)', transition: 'all 0.4s ease' }}>

          {/* Tag — neutral pill */}
          <div style={{
            display: 'inline-block',
            background: 'rgba(255,255,255,0.10)',
            color: 'rgba(255,255,255,0.85)',
            border: '1px solid rgba(255,255,255,0.18)',
            padding: '6px 16px', borderRadius: 20, fontSize: '0.85rem',
            fontWeight: 600, marginBottom: 24, letterSpacing: '0.5px',
          }}>
            {slide.tag}
          </div>

          {/* Title */}
          <h1 style={{
            fontFamily: "'Poppins', sans-serif", fontWeight: 800,
            fontSize: 'clamp(2.2rem, 5vw, 3.8rem)',
            color: '#ffffff', lineHeight: 1.2, marginBottom: 16, maxWidth: 800,
          }}>
            Dennis Kuria{' '}
            <span style={{ color: E, display: 'block' }}>{slide.title}</span>
          </h1>

          {/* Subtitle */}
          <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 'clamp(1rem, 2vw, 1.15rem)', maxWidth: 620, marginBottom: 40, lineHeight: 1.8 }}>
            {slide.subtitle}
          </p>

          {/* CTAs */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <a href={slide.href} style={{
              background: E, color: '#fff', padding: '14px 32px',
              borderRadius: 8, fontWeight: 700, fontSize: '0.95rem',
              transition: 'all 0.2s', display: 'inline-flex', alignItems: 'center', gap: 8,
              boxShadow: '0 4px 20px rgba(124,77,255,0.35)',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = ED; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = E;  e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              {slide.cta} <i className="fas fa-arrow-right" />
            </a>
            <a href="/#contact" style={{
              background: 'transparent', color: '#fff', padding: '14px 32px',
              borderRadius: 8, fontWeight: 600, fontSize: '0.95rem',
              border: '2px solid rgba(255,255,255,0.30)',
              transition: 'all 0.2s', display: 'inline-block',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.65)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.30)'; }}
            >
              Get In Touch
            </a>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24, marginTop: 80, borderTop: '1px solid rgba(255,255,255,0.10)', paddingTop: 40 }} className="stats-grid">
          {stats.map(s => (
            <div key={s.label} style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: '2rem', color: E }}>{s.value}</div>
              <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.85rem', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Dots */}
        <div style={{ display: 'flex', gap: 10, marginTop: 40 }}>
          {slides.map((_, i) => (
            <button key={i} onClick={() => goTo(i)} style={{
              width: i === current ? 32 : 10, height: 10,
              borderRadius: 5, background: i === current ? E : 'rgba(255,255,255,0.25)',
              border: 'none', cursor: 'pointer', transition: 'all 0.3s ease', padding: 0,
            }} />
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div style={{ position: 'absolute', bottom: 30, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, animation: 'bounce 2s infinite' }}>
        <span style={{ color: 'rgba(255,255,255,0.30)', fontSize: '0.75rem', letterSpacing: 2 }}>SCROLL</span>
        <i className="fas fa-chevron-down" style={{ color: 'rgba(255,255,255,0.40)', fontSize: '0.9rem' }} />
      </div>

      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50%       { transform: translateX(-50%) translateY(6px); }
        }
        @media (max-width: 600px) { .stats-grid { grid-template-columns: repeat(2, 1fr) !important; } }
      `}</style>
    </section>
  );
}
